import React, { FC, useEffect, useRef, useState } from 'react';

import { FormControl, TextField } from '@mui/material';
import Typography from '@mui/material/Typography';
import { alpha, styled } from '@mui/material/styles';

import { RadioQuestionProps } from '~/components/Test/types';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { ALPHABET } from '~/pages/const';
import { SMALL_MARGIN_PX, SUBMIDDLE_MARGIN_PX, TINY_MARGIN_PX } from '~/theme';
import AnimatedCheckmark from '~/ui/components/AnimatedCheckmark/AnimatedCheckmark';

interface TypeResponseQuestionProps extends Omit<RadioQuestionProps, 'colorsMode'> {}

const FIELD_ID = 'responseField';

const TypeResponseQuestion: FC<TypeResponseQuestionProps> = ({
  answers,
  onSelect,
  submitted,
  isCorrect,
}) => {
  const { t } = useTenantTranslation();
  const [value, setValue] = useState('');
  const answersByLetter = Object.fromEntries(
    answers.map((answer, index) => [ALPHABET[index], answer]),
  );
  const allowedLetters = Object.keys(answersByLetter);
  const selectedAnswer = answersByLetter[value];
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (selectedAnswer) {
      onSelectRef.current(selectedAnswer.id);
    } else {
      onSelectRef.current(null);
    }
  }, [selectedAnswer, onSelectRef]);

  return (
    <>
      {Object.entries(answersByLetter).map(([letter, answer]) => (
        <Li key={letter} data-testid="quizQuestionAnswer">
          {letter}. {answer?.title}
        </Li>
      ))}
      <StyledFormControl>
        <StyledLabel htmlFor={FIELD_ID} data-testid="responseFieldLabel">
          {t('page.test.typeIn', {
            letters: allowedLetters.slice(0, -1).join(', '),
            letter: allowedLetters.at(-1),
          })}
        </StyledLabel>
        <InputWrapper>
          <Input
            id={FIELD_ID}
            onChange={(e) => setValue(e.target.value.slice(0, 1).toUpperCase())}
            value={value}
            error={submitted && !isCorrect}
            correct={isCorrect}
            submitted={submitted}
            data-testid="responseField"
            disabled={submitted}
            autoComplete="off"
          />
          {submitted && (
            <Results correct={isCorrect} data-testid="resultFeedback">
              <StyledAnimatedCheckmark noBackground={true} correct={isCorrect} />
              <Caption data-testid="resultCaption">
                {isCorrect ? t('page.test.yourAnswerIsCorrect') : t('page.test.yourAnswerIsWrong')}
              </Caption>
            </Results>
          )}
        </InputWrapper>
      </StyledFormControl>
    </>
  );
};

export default TypeResponseQuestion;

const Li = styled('li')`
  color: ${({ theme }) => theme.palette.secondary.contrastText};
  margin-bottom: ${TINY_MARGIN_PX};
  font-size: 18px;
  font-weight: 700;
`;
const StyledLabel = styled('label')`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.common.text.secondaryToWhite};
`;
const StyledFormControl = styled(FormControl)`
  margin-top: ${SUBMIDDLE_MARGIN_PX};
  gap: ${SMALL_MARGIN_PX};
`;
const Input = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'correct' && prop !== 'submitted',
})<{
  correct?: boolean;
  submitted?: boolean;
}>`
  max-width: 56px;
  text-align: center;

  .MuiOutlinedInput-input {
    color: ${({ theme }) => theme.palette.secondary.contrastText};
  }

  .Mui-disabled {
    -webkit-text-fill-color: ${({ theme }) => theme.palette.secondary.contrastText};
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme, correct, submitted }) =>
      submitted
        ? correct && theme.palette.common.green
        : alpha(theme.palette.secondary.contrastText, 0.33)};
  }
`;
const InputWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    align-items: center;
    flex-direction: row;
    gap: ${SUBMIDDLE_MARGIN_PX};
  }
`;
const StyledAnimatedCheckmark = styled(AnimatedCheckmark)`
  width: 32px;
  height: 32px;
  margin-right: ${TINY_MARGIN_PX};
`;
const Results = styled('div', {
  shouldForwardProp: (prop) => prop !== 'correct',
})<{ correct?: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ theme, correct }) =>
    correct ? theme.palette.common.green : theme.palette.common.status.error};
`;
const Caption = styled(Typography)`
  font-size: 18px;
  font-weight: 700;
`;
